import React from "react";

import { Loader2, ExternalLink, ChevronRight } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface OptiSignsAccountChoiceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onUseCurrentAccount: () => void;
  onSignInToAnother: () => void;
  isConnecting: boolean;
  isCheckingKey?: boolean;
  isCheckingChanges?: boolean;
}

const LOADING_MESSAGES = {
  checkingChanges: {
    title: "Checking for unpublished changes...",
    subtitle: "Verifying your project status",
  },
  checkingKey: {
    title: "Checking OptiSigns connection...",
    subtitle: "Verifying your workspace API key",
  },
} as const;

export function OptiSignsAccountChoiceDialog({
  isOpen,
  onClose,
  onUseCurrentAccount,
  onSignInToAnother,
  isConnecting,
  isCheckingKey = false,
  isCheckingChanges = false,
}: OptiSignsAccountChoiceDialogProps): React.ReactElement {
  const isLoading = isCheckingChanges || isCheckingKey;
  const loadingMessage = isCheckingChanges
    ? LOADING_MESSAGES.checkingChanges
    : LOADING_MESSAGES.checkingKey;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="w-[calc(100%-2rem)] max-w-[440px] p-0 gap-0 overflow-hidden rounded-xl">
        {/* Header */}
        <div className="px-5 pt-5 pb-4 border-b border-border/50">
          <DialogHeader className="space-y-1.5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted border border-border">
                <img
                  src="/optisigns-logo.svg"
                  alt="OptiSigns"
                  className="w-5 h-5"
                />
              </div>
              <div>
                <DialogTitle className="text-base font-semibold">
                  Publish to OptiSigns
                </DialogTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Select an account to continue
                </p>
              </div>
            </div>
          </DialogHeader>
        </div>

        {/* Content */}
        <div className="px-4 py-3">
          {/* Loading State */}
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary mb-3" />
              <p className="text-sm font-medium text-foreground">
                {loadingMessage.title}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {loadingMessage.subtitle}
              </p>
            </div>
          ) : (
            <>
              {/* Current Session - Always First */}
              <div className="mb-3">
                <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider px-1 mb-2">
                  Current Session
                </p>
                <button
                  onClick={onUseCurrentAccount}
                  className="w-full group flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 p-3 text-left transition-all hover:bg-primary/10 hover:border-primary/50 active:scale-[0.99] focus:outline-none focus:ring-1 focus:ring-primary/50"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted border border-border">
                    <img
                      src="/optisigns-logo.svg"
                      alt="OptiSigns"
                      className="w-4 h-4"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">
                      Use current account
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      Continue with your active session
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-primary/50 group-hover:text-primary transition-colors" />
                </button>
              </div>

              {/* Add New Account */}
              <div>
                <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider px-1 mb-2">
                  Other Account
                </p>
                <button
                  onClick={onSignInToAnother}
                  disabled={isConnecting}
                  className="w-full group flex items-center gap-3 rounded-lg border border-dashed border-border p-3 text-left transition-all hover:border-foreground/30 hover:bg-accent/30 active:scale-[0.99] focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-dashed border-muted-foreground/30">
                    {isConnecting ? (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    ) : (
                      <ExternalLink className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">
                      Sign in to another account
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      Link a different OptiSigns account
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/40 group-hover:text-foreground/60 transition-colors" />
                </button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
